// Prochaine action en deux gestes (conception du suivi des affaires, § 4) : le type, puis l'échéance en un clic ;
// l'enregistrement se fait au choix de l'échéance. La note est facultative, sauf pour « Autre ».
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Check, Copy } from "lucide-react";
import { type Identifier, useNotify, useTranslate } from "ra-core";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Deal } from "../types";
import {
  DUE_CHOICES,
  dueDate,
  NEXT_ACTION_TYPES,
  type NextActionType,
  today,
} from "./nextAction";

export type NextActionValues = {
  next_action_type: NextActionType | null;
  next_action_date: string | null;
  next_action: string | null;
  next_action_contact_id: Identifier | null;
  next_action_message?: string | null;
};

/** Écrit la prochaine action et rafraîchit les affaires, « Ma journée » et le tableau de bord. Les affaires sont
 * relues avant de rendre la main, pour qu'un éditeur rouvert juste après parte de l'action à jour. */
export const useSaveNextAction = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const save = async (
    dealId: Identifier,
    values: NextActionValues,
    { quiet = false }: { quiet?: boolean } = {},
  ) => {
    setBusy(true);
    const { error } = await getSupabaseClient()
      .from("deals")
      .update(values)
      .eq("id", dealId);
    if (error) {
      setBusy(false);
      notify(error.message, { type: "error" });
      return false;
    }
    queryClient.invalidateQueries({ queryKey: ["my_day"] });
    await queryClient.invalidateQueries({ queryKey: ["deals"] });
    setBusy(false);
    if (!quiet)
      notify("resources.deals.next_action_editor.saved", { type: "info" });
    return true;
  };
  return { save, busy };
};

/** Libellé du type puis la note ; une action d'avant, sans type, garde son texte. */
export const nextActionText = (
  deal: Pick<Deal, "next_action_type" | "next_action">,
  translate: (key: string, options?: object) => string,
) => {
  const label =
    deal.next_action_type && deal.next_action_type !== "other"
      ? translate(`resources.deals.next_action_types.${deal.next_action_type}`)
      : null;
  return [label, deal.next_action].filter(Boolean).join(" — ");
};

export const NextActionEditor = ({
  deal,
  onDone,
  compact = false,
  open: openProp,
  onOpenChange,
}: {
  deal: Deal;
  onDone?: () => void;
  compact?: boolean;
  // Ouverture pilotée de l'extérieur (le journal l'ouvre après « a répondu » ou « Fait »).
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const t = (key: string, options?: object) =>
    translate(`resources.deals.next_action_editor.${key}`, options);
  const { save, busy } = useSaveNextAction();
  const [ownOpen, setOwnOpen] = useState(false);
  const open = openProp ?? ownOpen;
  const setOpen = (value: boolean) => {
    setOwnOpen(value);
    onOpenChange?.(value);
  };
  const [type, setType] = useState<NextActionType>(
    deal.next_action_type ?? "follow_up",
  );
  const [note, setNote] = useState(deal.next_action ?? "");
  // À chaque ouverture, et si l'affaire change pendant qu'il est ouvert (action effacée par « Fait » ou « A
  // répondu »), repartir de l'action en cours ; sans action, du type par défaut et d'une note vide.
  useEffect(() => {
    if (!open) return;
    setType(deal.next_action_type ?? "follow_up");
    setNote(deal.next_action ?? "");
  }, [open, deal.id, deal.next_action_type, deal.next_action]);
  const noteMissing = type === "other" && !note.trim();

  const pick = async (date: string) => {
    if (noteMissing) return;
    const done = await save(deal.id, {
      next_action_type: type,
      next_action_date: date,
      next_action: note.trim() || null,
      // Une personne visée n'a de sens que pour l'action qu'elle accompagnait.
      next_action_contact_id:
        type === deal.next_action_type
          ? (deal.next_action_contact_id ?? null)
          : null,
    });
    if (done) {
      setOpen(false);
      onDone?.();
    }
  };

  return (
    // Sur une carte du tableau Kanban, un clic dans l'éditeur ne doit ni ouvrir l'affaire ni la déplacer.
    <span
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={compact ? "h-6 px-2 text-xs" : "h-7 px-2"}
          >
            <CalendarClock className="size-3.5" />
            {deal.next_action_date ? t("change") : t("set")}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-72 flex flex-col gap-3 p-3"
          collisionPadding={16}
        >
          <Select
            value={type}
            onValueChange={(value) => setType(value as NextActionType)}
          >
            <SelectTrigger aria-label={t("type")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {NEXT_ACTION_TYPES.map((value) => (
                <SelectItem key={value} value={value}>
                  {translate(`resources.deals.next_action_types.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            value={note}
            maxLength={200}
            placeholder={t("note")}
            onChange={(event) => setNote(event.target.value)}
          />
          {noteMissing && (
            <p className="text-xs text-destructive">{t("note_required")}</p>
          )}
          <div className="flex flex-wrap gap-1.5">
            {DUE_CHOICES.map((choice) => (
              <Button
                key={choice}
                variant="secondary"
                size="sm"
                disabled={busy || noteMissing}
                onClick={() => pick(dueDate(choice, today()))}
              >
                {translate(`resources.deals.due.${choice}`)}
              </Button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            {t("other_day")}
            <input
              type="date"
              min={today()}
              disabled={busy || noteMissing}
              className="rounded border px-2 py-1 text-sm text-foreground"
              onChange={(event) =>
                event.target.value && pick(event.target.value)
              }
            />
          </label>
        </PopoverContent>
      </Popover>
    </span>
  );
};

/** « Fait » : la suite (saisie d'un échange) est branchée par le journal, tâche 5. */
export const DoneButton = ({ onClick }: { onClick: () => void }) => {
  const translate = useTranslate();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 px-2"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      <Check className="size-3.5" />
      {translate("resources.deals.next_action_editor.done")}
    </Button>
  );
};

/** Bouton « Copier le message » d'une action « 1er message LinkedIn ». */
export const LinkedInMessageCopy = ({ message }: { message: string }) => {
  const translate = useTranslate();
  const notify = useNotify();
  return (
    <Button
      size="sm"
      variant="outline"
      title={translate("resources.deals.linkedin_message.hint")}
      onClick={async () => {
        await navigator.clipboard.writeText(message);
        notify("resources.projects.assistant.copied", { type: "info" });
      }}
    >
      <Copy />
      {translate("resources.deals.linkedin_message.copy")}
    </Button>
  );
};
